export type StockPartition = {
  id: string;
  stats: {
    total: number;
    allocated: number;
  };
  stockPool: {
    product: {
      id: string;
      productGroup: { name: string | null } | null;
    };
  };
};

type BaseProduct = {
  allocation: string;
  external_id: string | null;
  image: string;
  name: string;
  shade: string | null;
  category: string | null;
};

export type Product =
  | {
      single: BaseProduct & {
        sample_type: string;
      };
    }
  | {
      singleBundle: { products: Omit<BaseProduct, "external_id">[] };
    }
  | {
      simpleGrid: { products: (BaseProduct & { display_index: number })[] };
    }
  | {
      swatchSelector: { products: (BaseProduct & { display_index: number })[] };
    }
  | {
      multiBundle: {
        products: {
          image: string;
          name: string;
          items: Omit<BaseProduct, "image">[];
        }[];
      };
    };

export type IndividualProduct = BaseProduct & {
  allocationIds: string[];
  hasStock: boolean;
  productGroupName: string | null;
  display_index?: number;
};

// Checks if a single product is in stock, used to check if a product in the product select
// is available
export function partitionInStock(stockPartition: StockPartition) {
  return stockPartition.stats.allocated < stockPartition.stats.total;
}

export function productInStock(
  allocation: string,
  stockPartitions: StockPartition[],
) {
  return stockPartitions.some(
    (stockPartition) =>
      stockPartition.id === allocation && partitionInStock(stockPartition),
  );
}

export function getProductId(
  allocation: string,
  stockPartitions: StockPartition[],
) {
  const stockPartition = stockPartitions.find(({ id }) => id === allocation);

  return stockPartition?.stockPool.product.id;
}

export function getProductGroupName(
  allocation: string,
  stockPartitions: StockPartition[],
): string | null {
  const stockPartition = stockPartitions.find(({ id }) => id === allocation);

  return stockPartition?.stockPool.product.productGroup?.name ?? null;
}

// Checks if any products are in stock, if the activity is out of stock it is no longer valid and
// we should redirect to the out of stock page
export function hasAvailableStock(stockPartitions: StockPartition[]) {
  if (!stockPartitions) return false;

  return stockPartitions.some(partitionInStock);
}

export function formatProducts(
  products: Product,
  stockPartitions: StockPartition[],
) {
  if ("single" in products) {
    return {
      type: "single",
      product: {
        ...products.single,
        allocationIds: [products.single.allocation],
        hasStock: productInStock(products.single.allocation, stockPartitions),
        productIds: stockPartitions.map(
          (stockPartition) => stockPartition.stockPool.product.id,
        ),
        productGroupName: getProductGroupName(
          products.single.allocation,
          stockPartitions,
        ),
      },
    } as const;
  }

  if ("singleBundle" in products) {
    const bundleProducts = products.singleBundle.products;
    return {
      type: "bundle",
      product: {
        products: bundleProducts.map((product) => ({
          ...product,
          productGroupName: getProductGroupName(
            product.allocation,
            stockPartitions,
          ),
        })),
        allocationIds: bundleProducts.map((product) => product.allocation),
        hasStock: bundleProducts.every((product) =>
          productInStock(product.allocation, stockPartitions),
        ),
        productIds: stockPartitions.map(
          (stockPartition) => stockPartition.stockPool.product.id,
        ),
      },
    } as const;
  }

  // variants and choice of single products
  if ("simpleGrid" in products) {
    return {
      type: "multi",
      product: {
        products: products.simpleGrid.products.map((product) => ({
          ...product,
          allocationIds: [product.allocation],
          hasStock: productInStock(product.allocation, stockPartitions),
          productIds: [getProductId(product.allocation, stockPartitions)],
          productGroupName: getProductGroupName(
            product.allocation,
            stockPartitions,
          ),
        })),
      },
    } as const;
  }

  // choice of bundles
  if ("multiBundle" in products) {
    const bundleProducts = products.multiBundle.products;
    return {
      type: "multiBundle",
      product: {
        products: bundleProducts.map((product) => ({
          ...product,
          allocationIds: product.items.map((item) => item.allocation),
          hasStock: product.items.every((item) =>
            productInStock(item.allocation, stockPartitions),
          ),
          productIds: product.items.map((item) =>
            getProductId(item.allocation, stockPartitions),
          ),
          items: product.items.map((item) => ({
            ...item,
            productGroupName: getProductGroupName(
              item.allocation,
              stockPartitions,
            ),
          })),
        })),
      },
    } as const;
  }

  if ("swatchSelector" in products) {
    return {
      type: "multi",
      product: {
        products: products.swatchSelector.products.map((product) => ({
          ...product,
          allocationIds: [product.allocation],
          hasStock: productInStock(product.allocation, stockPartitions),
          productIds: [getProductId(product.allocation, stockPartitions)],
          productGroupName: getProductGroupName(
            product.allocation,
            stockPartitions,
          ),
        })),
      },
    } as const;
  }

  throw new Error(`Invalid product type: ${JSON.stringify(products)}`);
}

export type ProductType<T = string> = ReturnType<typeof formatProducts> & {
  type: T;
};

export function getAllocationIds(products: ProductType) {
  switch (products.type) {
    case "single":
    case "bundle":
      return {
        type: "single",
        allocationIds: products.product.allocationIds as string[],
      };
    case "multi":
    case "multiBundle":
      return {
        type: "multi",
        allocationIds: products.product.products?.flatMap(
          (product) => product.allocationIds,
        ),
      };
  }
}

export function getSelectedProductIds(
  products: ProductType,
  selectedAllocationIds: string[],
) {
  switch (products.type) {
    case "single":
    case "bundle":
      return products.product.productIds.toString();
    case "multi":
    case "multiBundle":
      const selectedProduct = products.product.products.find(
        ({ allocationIds }) => {
          // const sameAllocationIds = selectedAllocationIds.every(
          //   (allocationId) => allocationIds.includes(allocationId),
          // );
          return allocationIds.toString() === selectedAllocationIds.toString();
        },
      );
      return selectedProduct?.productIds.toString();
  }
}

export function getSelectedProductNames(
  products: ProductType,
  selectedAllocationIds: string[],
) {
  switch (products.type) {
    case "single":
      return products.product.name;
    case "bundle":
      const bundleProductNames = products.product.products.map(
        ({ name }) => name,
      );
      return bundleProductNames.toString();
    case "multi":
    case "multiBundle":
      const selectedProduct = products.product.products.find(
        ({ allocationIds }) => {
          // const sameAllocationIds = selectedAllocationIds.every(
          //   (allocationId) => allocationIds.includes(allocationId),
          // );
          return allocationIds.toString() === selectedAllocationIds.toString();
        },
      );
      return selectedProduct?.name;
  }
}
